import { IModel, Model, ModelRecord } from './Model';
import { IStoreModel, StoreModel } from './StoreModel';
import { IHospitalHoursModel, HospitalHoursModel } from './HospitalHoursModel';
import { computed, makeObservable, observable } from 'mobx';
import { StringUtility } from '@common/utils';
import {
  IHospitalScheduleTemplateModel,
  HospitalScheduleTemplateModel,
} from './HospitalScheduleTemplateModel';
import { IvlsDeviceModel } from './IvlsDeviceModel';
import {
  IHospitalSalesItemFavoritesModel,
  HospitalSalesItemFavoritesModel,
} from './HospitalSalesItemFavoritesModel';

export interface IHospitalModel extends IModel {
  hospitalId: number;
  storeId: number;
  store: IStoreModel | undefined;
  name: string;
  phone: string;
  email: string;
  isDisabled: boolean;
  code: string;
  url: string;
  fax: string;
  outboundSMSPhone: string;
  hospitalHours: IHospitalHoursModel[];
  parentOrganizationId: number | undefined;
  organizationRelationKey: string;
  diagnosticLabUserProvided?: boolean | undefined;
  diagnosticLabUserName?: string | undefined;
  diagnosticLabPassword?: string | undefined;
  isDoubleBookingEnabled: boolean;
  defaultScheduleTemplate: IHospitalScheduleTemplateModel | undefined;
  currentClinicId: number | undefined;
  ivlsDevices: IvlsDeviceModel[];
  outOfOfficeMessage?: string | undefined;
  isOutOfOfficeEnabled?: boolean | undefined;
  awayMessage?: string | undefined;
  awayMessageEnabled?: boolean | undefined;
  automatePostAnestheticFollowUp: boolean;
  hospitalSalesItemFavorites: IHospitalSalesItemFavoritesModel[];
}

export class HospitalModel implements ModelRecord<IHospitalModel> {
  @observable hospitalId: number;
  @observable storeId: number;
  @observable store: StoreModel | undefined;
  @observable name: string;
  @observable phone: string;
  @observable email: string;
  @observable code: string;
  @observable isDisabled: boolean;
  @observable url: string | undefined;
  @observable fax: string | undefined;
  @observable outboundSMSPhone: string;
  @observable hospitalHours: HospitalHoursModel[];
  @observable organizationRelationKey: string;
  @observable parentOrganizationId: number | undefined;
  @observable diagnosticLabUserProvided: boolean | undefined;
  @observable diagnosticLabUserName: string | undefined;
  @observable diagnosticLabPassword: string | undefined;
  @observable rowVersion: string;
  @observable isDoubleBookingEnabled: boolean;
  @observable defaultScheduleTemplate:
    | HospitalScheduleTemplateModel
    | undefined;
  @observable currentClinicId: number | undefined;
  @observable ivlsDevices: IvlsDeviceModel[];
  @observable outOfOfficeMessage: string | undefined;
  @observable isOutOfOfficeEnabled: boolean | undefined;
  @observable awayMessage: string | undefined;
  @observable awayMessageEnabled: boolean | undefined;
  @observable automatePostAnestheticFollowUp: boolean;
  @observable hospitalSalesItemFavorites: HospitalSalesItemFavoritesModel[];

  constructor(init?: IHospitalModel) {
    this.rowVersion = _.defaultTo(init?.rowVersion, '');
    this.hospitalId = _.defaultTo(init?.hospitalId, 0);
    this.code = _.defaultTo(init?.code, '');
    this.storeId = _.defaultTo(init?.storeId, 0);
    this.store = init?.store ? new StoreModel(init.store) : undefined;
    this.name = _.defaultTo(init?.name, '');
    this.phone = _.defaultTo(init?.phone, '');
    this.email = _.defaultTo(init?.email, '');
    this.isDisabled = _.defaultTo(init?.isDisabled, false);
    this.url = StringUtility.SanitizeString(init?.url);
    this.fax = StringUtility.SanitizeString(init?.fax);
    this.outboundSMSPhone = _.defaultTo(init?.outboundSMSPhone, '');
    this.organizationRelationKey = _.defaultTo(
      init?.organizationRelationKey,
      '',
    );
    this.diagnosticLabUserProvided = _.defaultTo(
      init?.diagnosticLabUserProvided,
      undefined,
    );
    this.diagnosticLabUserName = _.defaultTo(
      init?.diagnosticLabUserName,
      undefined,
    );
    this.diagnosticLabPassword = _.defaultTo(
      init?.diagnosticLabPassword,
      undefined,
    );
    this.hospitalHours = init?.hospitalHours
      ? init.hospitalHours.map((hh) => new HospitalHoursModel(hh))
      : ([] as HospitalHoursModel[]);
    this.isDoubleBookingEnabled = init?.isDoubleBookingEnabled ?? true;
    this.defaultScheduleTemplate = init?.defaultScheduleTemplate
      ? new HospitalScheduleTemplateModel(init?.defaultScheduleTemplate)
      : undefined;
    this.parentOrganizationId = _.defaultTo(
      init?.parentOrganizationId,
      undefined,
    );
    this.currentClinicId = _.defaultTo(init?.currentClinicId, undefined);
    this.ivlsDevices = init?.ivlsDevices
      ? init.ivlsDevices.map((hh) => new IvlsDeviceModel(hh))
      : ([] as IvlsDeviceModel[]);
    this.outOfOfficeMessage = _.defaultTo(init?.outOfOfficeMessage, '');
    this.isOutOfOfficeEnabled = _.defaultTo(init?.isOutOfOfficeEnabled, false);
    this.awayMessage = _.defaultTo(init?.awayMessage, '');
    this.awayMessageEnabled = _.defaultTo(init?.awayMessageEnabled, false);
    this.automatePostAnestheticFollowUp = _.defaultTo(
      init?.automatePostAnestheticFollowUp,
      true,
    );
    this.hospitalSalesItemFavorites = init?.hospitalSalesItemFavorites
      ? init.hospitalSalesItemFavorites.map(
          (f) => new HospitalSalesItemFavoritesModel(f),
        )
      : ([] as HospitalSalesItemFavoritesModel[]);

    makeObservable(this, undefined, { autoBind: true });
  }

  @computed get openDaysOfWeek(): number[] {
    //O.o get open days and map to day of week and then select unique day of week as an array of days of the week the hospital is open
    return this.hospitalHours
      .filter((a) => a.isOpen)
      .map((a) => a.dayOfWeek)
      .filter((item, i, ar) => ar.indexOf(item) === i);
  }

  @computed get openStartTime(): string {
    return '8:00'; //TODO get earliest open time for specific day
  }

  @computed get closeStartTime(): string {
    return '12:00'; //TODO get latest open time for specific day
  }

  @computed get hospitalFlags(): HospitalFlags {
    const flags: HospitalFlags = {
      isDoubleBookingEnabled: this.isDoubleBookingEnabled,
      isOutOfOfficeEnabled: this.isOutOfOfficeEnabled,
      automatePostAnestheticFollowUp: this.automatePostAnestheticFollowUp,
    };
    return flags;
  }
}

export interface IHospitalListingModel extends IModel {
  hospitalId: number;
  code: string;
  name: string;
  address: string;
  isDisabled: boolean;
  organizationRelationKey: string;
}

export class HospitalListingModel
  extends Model
  implements IHospitalListingModel
{
  hospitalId: number;
  code: string;
  name: string;
  address: string;
  isDisabled: boolean;
  organizationRelationKey: string;

  constructor(params: IHospitalListingModel) {
    super(params);
    this.hospitalId = params.hospitalId;
    this.code = params.code;
    this.name = params.name;
    this.address = params.address;
    this.isDisabled = params.isDisabled;
    this.organizationRelationKey = params.organizationRelationKey;
  }
}

export type HospitalFlags = Pick<
  HospitalModel,
  | 'isDoubleBookingEnabled'
  | 'isOutOfOfficeEnabled'
  | 'automatePostAnestheticFollowUp'
>;
